import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import PublicHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function SubmitStory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [pub, setPub] = useState(null);
  const [eventName, setEventName] = useState("");
  const [submitterType, setSubmitterType] = useState("lovers");
  const [locked, setLocked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loversAgree, setLoversAgree] = useState(false);
  const [photoPermission, setPhotoPermission] = useState(false);
  const [proPromises, setProPromises] = useState(false);
  const [openToEither, setOpenToEither] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const ev = await api.get(`/events/${id}/summary`);
        const p = ev.data.publication === "dwh" || ev.data.publication === "dwt" ? ev.data.publication : null;
        setPub(p);
        setEventName(ev.data.event_name || "");
        setSubmitterType(ev.data.submitter_type || "lovers");
        setLocked(ev.data.editable === false);
        const savedConsent = ev.data.consent || {};
        setTermsAccepted(!!savedConsent.terms_accepted);
        setLoversAgree(!!savedConsent.lovers_agree);
        setPhotoPermission(!!savedConsent.photo_permission);
        setProPromises(!!savedConsent.pro_promises);
        setOpenToEither(!!savedConsent.open_to_either);
        setMarketingOptIn(!!savedConsent.marketing_opt_in);
      } catch (err) {
        const message = formatApiError(err, "We could not load this submission.");
        setSubmitError(message);
        toast.error(message);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (pub) document.documentElement.dataset.pub = pub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [pub]);

  const isPro = submitterType === "pro";
  const pubFullName = pub === "dwt" ? "Dancing With Them" : "Dancing With Her";


  const submit = async () => {
    setSubmitError("");
    const missingAgreements = [];
    if (!termsAccepted) missingAgreements.push("Submission Terms and Privacy Policy");
    if (isPro && !proPromises) missingAgreements.push("permission from the lovers and photographer");
    if (!isPro && !loversAgree) missingAgreements.push("agreement from everyone named as a lover");
    if (!isPro && !photoPermission) missingAgreements.push("permission to use the photos");
    if (missingAgreements.length) {
      const message = `Before submitting, please confirm: ${missingAgreements.join("; ")}.`;
      setSubmitError(message);
      toast.error(message);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${id}/submit`, {
        terms_accepted: termsAccepted,
        lovers_agree: loversAgree,
        photo_permission: photoPermission,
        pro_promises: proPromises,
        marketing_opt_in: marketingOptIn,
        open_to_either: openToEither,
      });
      if (data?.status !== "submitted") {
        throw new Error("The server did not confirm that your submission was received. Please try again.");
      }
      navigate(`/create-event/${id}/done`);
    } catch (err) {
      const message = formatApiError(err, "We could not submit your story.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="submit-page" data-pub={pub || "neutral"}>
      <PublicHeader pub={pub} />

      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16">
        <p className="btw-step-label">Final step</p>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">Ready to Submit</h1>
        <p className="text-sm text-black leading-relaxed mb-10 max-w-xl">
          {eventName ? <>You're submitting <strong>{eventName}</strong>. </> : null}
          Our team will review your submission and we will be in touch with more
          information soon.
        </p>

        {locked && (
          <div className="bg-white border border-[#c19872] rounded-xl p-4 text-sm text-black mb-8" data-testid="locked-banner">
            This submission has already been sent and is with our editorial team.
          </div>
        )}

        <div className="btw-section space-y-5" data-testid="consent-section">
          <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
            <input
              type="checkbox"
              data-testid="consent-terms"
              className="btw-check mt-0.5"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <span>
              I've read and agree to the{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="underline" data-testid="terms-link">Submission Terms</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline" data-testid="privacy-link">Privacy Policy</a>. (required)
            </span>
          </label>
          {isPro ? (
            <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
              <input
                type="checkbox"
                data-testid="consent-pro-promises"
                className="btw-check mt-0.5"
                checked={proPromises}
                onChange={(e) => setProPromises(e.target.checked)}
              />
              <span><strong>The lovers in this submission agree to it being sent and published</strong>, and I have permission to submit these photos. (required)</span>
            </label>
          ) : (
            <>
              <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="consent-lovers-agree"
                  className="btw-check mt-0.5"
                  checked={loversAgree}
                  onChange={(e) => setLoversAgree(e.target.checked)}
                />
                <span><strong>Everyone named as a lover in this submission agrees</strong> to our story, names and photos being published, including details about our relationship. (required)</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="consent-photo-permission"
                  className="btw-check mt-0.5"
                  checked={photoPermission}
                  onChange={(e) => setPhotoPermission(e.target.checked)}
                />
                <span><strong>Our photographer has given permission</strong> for these photos to be submitted, and so has anyone clearly identifiable in them (a parent or guardian for any child). (required)</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="consent-open-to-either"
                  className="btw-check mt-0.5"
                  checked={openToEither}
                  onChange={(e) => setOpenToEither(e.target.checked)}
                />
                <span>We're open to our story being featured on either Dancing With Her or Dancing With Them. (optional)</span>
              </label>
            </>
          )}
          <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
            <input
              type="checkbox"
              data-testid="consent-marketing"
              className="btw-check mt-0.5"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
            />
            <span>Yes please, send me the {pubFullName} newsletter. I can unsubscribe any time. (optional)</span>
          </label>
        </div>

        {submitError && <div role="alert" className="mt-8 border border-[#b35c5c] bg-white p-4 text-sm text-[#9b3f3f]" data-testid="final-submit-error">{submitError}</div>}

        <div className="flex justify-end mt-4">
          <button type="button" onClick={submit} disabled={submitting || locked} data-testid="final-submit" className="btw-btn w-full sm:w-auto">
            <span>{submitting ? "Submitting…" : "Submit your story"}</span>
            <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
          </button>
        </div>
      </main>

      <SiteFooter pub={pub} />
    </div>
  );
}
